import { Box, Button, Skeleton, Stack, Typography } from '@mui/material'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import { IconTag } from '@tabler/icons-react'
import { useMemo } from 'react'
import { groupBy, omit, prop, sumBy } from 'remeda'
import { useGetInventory } from '~/features/hooks/useGetInventory'
import { ImportInventory } from '~/features/inventory/components/ImportInventory'
import { TransferForm } from '~/features/inventory/components/TransferForm'
import { InventoryListRowModel } from '~/features/inventory/types'
import { SelectionPanel, SelectionPanelAction } from '~/ui/components/SelectionPanel'
import { useDataGridProps } from '~/ui/hooks/useDataGridProps'
import { CustomDataGrid } from '~/ui/components/CustomDataGrid'
import { DownloadImportFileLink } from '~/features/inventory/components/DownloadImportFileLink'
import { OverviewCard } from '~/ui/components/OverviewCard'
import { useGetShopifyProducts } from '../shopify/hooks/useGetShopifyProducts'

const commonColumnProps = {
  flex: 1,
}

export const InventoryList = () => {
  const { data: inventoryData, isLoading: isLoadingInventory } = useGetInventory({
    filters: [{ column: 'status', operator: 'eq', value: 'available' }],
  })

  const columns = useMemo<GridColDef<InventoryListRowModel>[]>(() => {
    return [
      {
        field: 'stock_code_v2',
        headerName: 'Stock code',
        width: 150,
      },
      {
        field: 'supplier_code',
        headerName: 'Supplier code',
        width: 150,
      },
      {
        field: 'location',
        headerName: 'Location',
        width: 100,
      },
      {
        field: 'size',
        headerName: 'Size',
        width: 100,
      },
      {
        field: 'inventory_item_id',
        headerName: 'Inventory item id',
      },
      {
        field: 'product_variant_id',
        headerName: 'Product variant id',
      },
      {
        field: 'import_file_name',
        headerName: 'Import file',
        renderCell: (params: GridRenderCellParams<InventoryListRowModel>) => {
          const { import_file_name } = params.row

          return (
            <Stack sx={{ justifyContent: 'center', height: 1 }}>
              {import_file_name ? (
                <DownloadImportFileLink filename={import_file_name} />
              ) : (
                <Typography variant="body2">none</Typography>
              )}
            </Stack>
          )
        },
        ...commonColumnProps,
      },
    ]
  }, [])

  const rows = useMemo<InventoryListRowModel[]>(() => {
    if (!inventoryData) return []

    return inventoryData.map((el) => ({ ...el, ...omit(el.models_v2, ['status', 'id']) }))
  }, [inventoryData])

  const { data: shopifyData, isLoading: isLoadingShopify } = useGetShopifyProducts()

  const shopifyQuantity = useMemo(() => {
    if (!shopifyData) return 0
    const flattened = shopifyData.flatMap((el) => el.variants.nodes)
    return sumBy(flattened, (el) => el.inventoryQuantity)
  }, [shopifyData])
  const cardData = useMemo(() => {
    if (!inventoryData) return { wollem: [], mandarin: [] }
    const grouped = groupBy(inventoryData, prop('location'))

    return grouped
  }, [inventoryData])

  const { selectedRows, exportDatagrid, reset, onRowSelectionModelChange, selectionModel } =
    useDataGridProps<InventoryListRowModel>({
      rows,
    })

  const isLoading = isLoadingInventory

  const actions: SelectionPanelAction[] = useMemo(
    () => [
      {
        key: 'transfer',
        Component: () => (
          <TransferForm
            rows={selectedRows}
            onClose={reset}
            disabled={selectedRows.some((el) => el.location !== selectedRows[0]?.location)}
          />
        ),
      },
    ],
    [reset, selectedRows]
  )

  return (
    <Stack gap={1} sx={{ flex: 1 }}>
      <Stack direction="row" gap={2} justifyContent="space-between">
        <Stack direction="row" gap={2} alignItems="flex-start">
          <OverviewCard
            Icon={IconTag}
            value={<Typography fontWeight="bold">{cardData.wollem?.length ?? 0}</Typography>}
            description="Wollem stock"
            color="secondary"
          />

          <OverviewCard
            Icon={IconTag}
            value={<Typography fontWeight="bold">{cardData.mandarin?.length ?? 0}</Typography>}
            description="Mandarin stock"
            color="secondary"
          />
          <OverviewCard
            Icon={IconTag}
            value={
              isLoadingShopify ? (
                <Skeleton width={50} height={20} />
              ) : (
                <Typography fontWeight="bold">{shopifyQuantity}</Typography>
              )
            }
            description="Shopify stock"
            color="secondary"
          />
        </Stack>

        <Stack gap={1} sx={{ justifyContent: 'center', height: 1 }}>
          <ImportInventory />
          <Button variant="outlined" onClick={exportDatagrid} disabled={selectedRows.length === 0}>
            Export
          </Button>
        </Stack>
      </Stack>

      <Stack sx={{ height: 1 }}>
        <Box sx={{ position: 'relative', flex: 1, height: 1 }}>
          <CustomDataGrid
            rows={rows}
            columns={columns}
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={onRowSelectionModelChange}
            disableRowSelectionOnClick
            checkboxSelection
            loading={isLoading}
          >
            <SelectionPanel rows={selectedRows} actions={actions} onClose={reset} />
          </CustomDataGrid>
        </Box>
      </Stack>
    </Stack>
  )
}
